import { useAppDispatch, useAppSelector } from 'store/store-hooks';
import { useLocation, Path } from 'components/_hooks/use-router';
import { selectClipData } from 'components/app/_helpers/_app.helper';
import { Error } from 'components/app/_helpers/app-errors.helper';
import {
  generateLinkId,
  isLinked,
  isLinkingPossible,
  selectLinkedGroup,
} from 'components/app/editor/_mediator/editor-clips/editor-clips-linking.helper';
import { handleSbLinks } from 'components/app/editor/_mediator/editor-clips/editor-clips-sb.helper';
import { LinkedGroupId, setSelectedClips, handleClips, Clip } from 'components/app/editor/_mediator/editor.slice';
import { linksApi } from './editor-clips.helper';

export const useClipsLinking = (clipData?: Clip) => {
  const { clipsMap, linkedClips, selectedClips, storyBoard } = useAppSelector((state) => state.editor);
  const { path } = useLocation();
  const dispatch = useAppDispatch();

  const linkClips = () => {
    if (!selectedClips.length || !clipsMap) return;

    const linked: Clip[] = [];
    const unlinked: Clip[] = [];
    const clips = selectedClips.map((clipId) => selectClipData(clipId, clipsMap));

    clips.forEach((clip) => {
      if (isLinked(clip.clipGroupId)) linked.push(clip);
      else unlinked.push(clip);
    });
    if (!unlinked.length) return;

    const linkId = linked[0]?.clipGroupId ?? generateLinkId(linkedClips);
    const res = linksApi(linkedClips).link(unlinked, linkId);

    const res1 = handleSbLinks(res.linkedClips, storyBoard, storyBoard);
    if (res1.status === 'cantBeFixed') {
      Error.handleSbLinks();
      return;
    }
    dispatch(
      handleClips({
        data: { ...res, storyBoard: res1.storyBoard },
        backupParams: { title: 'Link Clips' },
      })
    );
    dispatch(setSelectedClips([]));
  };

  const unlinkClips = () => {
    if (!clipsMap || !clipData) return;
    if (!isLinked(clipData.clipGroupId)) return;
    const clipIds = selectLinkedGroup(clipData.clipGroupId, linkedClips);
    const clips = clipIds.map((clipId) => selectClipData(clipId, clipsMap));
    const res = linksApi(linkedClips).unlink(clips);
    dispatch(handleClips({ data: res, backupParams: { title: 'Unlink Clips' } }));
    dispatch(setSelectedClips([]));
  };

  const linkIsDisabled = () => {
    if (!clipData || !clipsMap) return;
    if (path !== Path.selector || !selectedClips.length || !selectedClips.includes(clipData.clipId)) return true;
    if (!isLinkingPossible(selectedClips)) return true;

    const linkGroups: LinkedGroupId[] = [];
    const unlinkedClips: ClipId[] = [];
    selectedClips.forEach((clipId) => {
      const clipData = selectClipData(clipId, clipsMap);
      if (!isLinked(clipData?.clipGroupId)) unlinkedClips.push(clipId);
      else if (!linkGroups.includes(clipData?.clipGroupId)) linkGroups.push(clipData?.clipGroupId);
    });
    if (linkGroups.length > 1) return true;
    if (!unlinkedClips.length) return true;
  };

  const unlinkIsDisabled = () => {
    if (!clipsMap || !clipData) return;
    return !isLinked(clipData.clipGroupId);
  };

  return { linkClips, unlinkClips, linkIsDisabled, unlinkIsDisabled };
};
